import axios from "axios";
import dayjs from "dayjs";
import { message } from "antd";
import i18n from "i18next";
import { PAYMENT_METHOD_INVOICE } from "../constants/PaymentConstants";
import { DEFAULT_DELIVERY_LABEL_FALLBACK, RETAIL_DELIVERY_LABEL } from "../constants/OrderConstant";
import { applyCustomerPricingToProduct, calculateOrderPricing } from "./pricing";
import { NETHERLANDS_COUNTRY_CODE } from "../constants/PricingConstants";
import { GROWY } from "../constants/CompanyConstants";
import { getFirebaseIDToken } from "./firebase/firebase";
import { describeEdifactMessage, generateEdifactAperak, generateEdifactDesadv, previewEdifactDesadv, sendEdifactDesadv, sendEdifactInvoic } from "../urls";

// Moet gelijk blijven aan MAX_LAYERS_PER_PALLET in api/src/edifact/desadv-constants.ts.
const MAX_LAYERS_PER_PALLET = 16;
// ponytail: hardcoded GTIN van het pallet-product (42-0002), naar een setting zodra er meer pallettypes zijn.
const PALLET_GTIN = "3589460000029";

/**
 * Zet het geparste EDIFACT ORDERS-resultaat (van POST /api/edifact/orders) om
 * naar dezelfde farmorder-JSON-vorm als parseExcelOrders(), zodat dezelfde
 * preview/confirm-flow (PreviewExcelOrdersModal) hergebruikt kan worden.
 *
 * In tegenstelling tot de Excel-import wordt een order met een onbekend
 * product (GTIN zonder match op "barcode") NIET in zijn geheel overgeslagen:
 * de order verschijnt in finalOrders met de bekende regels in de shopping
 * cart en een edifactUnknownLines-lijst met de onbekende regels, zodat de
 * gebruiker dit kan zien vóórdat de order definitief wordt bevestigd.
 */
export const parseEdifactOrder = (
  edifactResult,
  customers,
  products,
  auth,
  companyFilter,
  priceLists,
  countriesMap = null,
) => {
  const skippedOrders = [];

  if (!edifactResult?.success) {
    return { finalOrders: [], skippedOrders: [] };
  }

  const reasons = [];

  // Klant matchen op de buyer-GLN die de backend al tegen de klantendatabase heeft gelegd.
  const dbCustomer = edifactResult.buyerCustomer?.customer
    ? customers.find((c) => c.id === edifactResult.buyerCustomer.customer.id)
    : null;

  if (!dbCustomer) {
    reasons.push(
      edifactResult.buyerGln
        ? `Geen klant gevonden met GLN "${edifactResult.buyerGln}"`
        : "Geen afnemer-GLN (NAD+BY) gevonden in het bericht",
    );
    skippedOrders.push({
      order: { customer: edifactResult.buyerGln || "Onbekend", edifactOrderNumber: edifactResult.orderNumber },
      reasons,
    });
    return { finalOrders: [], skippedOrders };
  }

  if (!edifactResult.requestedDeliveryDate) {
    reasons.push("Geen gewenste leverdatum (DTM+2) gevonden in het bericht");
    skippedOrders.push({ order: { customer: dbCustomer.customerName, edifactOrderNumber: edifactResult.orderNumber }, reasons });
    return { finalOrders: [], skippedOrders };
  }

  const isGrowy = companyFilter === GROWY;

  // Regels opnieuw matchen tegen de volledige, actuele productendatabase (i.p.v. de
  // ingekorte productvelden die de backend teruggeeft) zodat prijsvelden (o.a. "price")
  // beschikbaar zijn voor de pricing-berekening, net als bij de Excel-import.
  // Producten kunnen zowel met (14, "barcode") als zonder (13, ruw GTIN) voorloopnul
  // zijn opgeslagen — probeer op beide te matchen.
  const linesWithDbProduct = edifactResult.lines.map((line) => ({
    ...line,
    dbProduct: products.find((p) => p.barcode === line.barcode || p.barcode === line.gtin) || null,
  }));

  const knownLines = linesWithDbProduct.filter((line) => line.dbProduct);
  const unknownLines = linesWithDbProduct.filter((line) => !line.dbProduct);

  const userShoppingCart = knownLines.map((line) => {
    const productPrice = applyCustomerPricingToProduct(line.dbProduct, dbCustomer, priceLists, countriesMap);
    return {
      itemCount: line.orderedQuantity || 0,
      productGroup: line.dbProduct.productGroup,
      productId: line.dbProduct.productId,
      productName: line.dbProduct.productName,
      productPrice,
      size: line.dbProduct.size,
    };
  });

  // Pallets ook op de order (zelfde berekening als de DESADV): één artikel per pallet,
  // besteld aantal = aantal kratten (1 HE = 1 krat), kratten per pallet = cratesPerLayer x 16.
  const palletWarnings = [];
  const palletCount = knownLines.reduce((sum, line) => {
    const cratesPerPallet = (line.dbProduct.cratesPerLayer || 0) * MAX_LAYERS_PER_PALLET;
    if (!cratesPerPallet) {
      palletWarnings.push({
        code: "NO_CRATES_PER_LAYER",
        message: `Geen cratesPerLayer ingesteld voor "${line.dbProduct.productName}" — pallets voor deze regel niet berekend`,
      });
      return sum;
    }
    return sum + Math.ceil((line.orderedQuantity || 0) / cratesPerPallet);
  }, 0);
  if (palletCount > 0) {
    const palletProduct = products.find((p) => p.barcode === PALLET_GTIN || p.barcode === "0" + PALLET_GTIN);
    if (palletProduct) {
      userShoppingCart.push({
        itemCount: palletCount,
        productGroup: palletProduct.productGroup,
        productId: palletProduct.productId,
        productName: palletProduct.productName,
        productPrice: applyCustomerPricingToProduct(palletProduct, dbCustomer, priceLists, countriesMap),
        size: palletProduct.size,
      });
    } else {
      palletWarnings.push({
        code: "UNKNOWN_PALLET_PRODUCT",
        message: `Geen pallet-product gevonden met GTIN "${PALLET_GTIN}" — ${palletCount} pallet(s) niet op de order gezet`,
      });
    }
  }

  const pricing = calculateOrderPricing(
    userShoppingCart,
    dbCustomer,
    isGrowy ? RETAIL_DELIVERY_LABEL : dbCustomer?.deliveryLabel || DEFAULT_DELIVERY_LABEL_FALLBACK,
    products,
    true,
    null,
    priceLists,
    countriesMap,
  );

  const formattedSubTotal = parseFloat(pricing.subtotal.toFixed(2));
  const formattedTotalPrice = parseFloat(pricing.total.toFixed(2));
  const tax = parseFloat(pricing.tax.toFixed(2));

  const customerCountry = dbCustomer.country || NETHERLANDS_COUNTRY_CODE;
  const countrySettings = countriesMap?.[customerCountry];
  const deliveryCostThreshold = countrySettings?.delivery?.deliveryCostThreshold || 50;

  const finalOrder = {
    crates: 1,
    createdBy: "CMS EDIFACT import - " + auth.email,
    customerDiscount: 0,
    deliveryBy: isGrowy ? "Growy" : "Chef's Farm",
    deliveryCostThreshold,
    deliveryLabel: isGrowy
      ? dbCustomer?.deliveryLabel || RETAIL_DELIVERY_LABEL
      : dbCustomer?.deliveryLabel || DEFAULT_DELIVERY_LABEL_FALLBACK,
    deliveryNotes: "",
    deliveryTime: "",
    deliveryTimeCharge: 0,
    farmorderComments: "",
    farmorderCustomerId: dbCustomer?.id || "",
    farmorderCustomerName: dbCustomer?.customerName,
    farmorderDateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
    farmorderDeliveryDate: dayjs(edifactResult.requestedDeliveryDate).format("YYYY-MM-DD"),
    farmorderId: "",
    farmorderRegion: dbCustomer.customerRegion,
    farmorderStatus: "submitted",
    farmorderUserFirstName: "",
    farmorderUserId: "",
    farmorderUserLastName: "",
    invoiceAdditionalCosts: 0,
    orderDateTime: dayjs().unix() * 1000,
    packed: false,
    paymentMethod: PAYMENT_METHOD_INVOICE,
    reference: edifactResult.orderNumber || "",
    shippingCost: pricing.shippingCost.toString(),
    subTotalAmount: formattedSubTotal,
    subTotalWithoutDiscount: formattedTotalPrice,
    tax,
    totalAmount: formattedTotalPrice,
    userEmail: auth.email,
    userLanguage: "nl",
    userShoppingCart,
    // EDIFACT-specifieke velden: edifactOrderNumber en edifactRawMessage worden mee
    // opgeslagen op de Farmorder (zie farmorder.entity.ts), zodat een APERAK later
    // altijd gegenereerd kan worden zonder het bericht opnieuw te hoeven invoeren.
    // edifactUnknownLines/edifactWarnings zijn alleen voor de preview-UI.
    edifactOrderNumber: edifactResult.orderNumber,
    edifactRawMessage: edifactResult.rawMessage,
    edifactUnknownLines: unknownLines.map((line) => ({
      lineNumber: line.lineNumber,
      gtin: line.gtin,
      barcode: line.barcode,
      orderedQuantity: line.orderedQuantity,
    })),
    edifactWarnings: [...(edifactResult.warnings || []), ...palletWarnings],
  };

  return { finalOrders: [finalOrder], skippedOrders };
};

/**
 * Genereert de APERAK-ontvangstbevestiging voor een order die via EDIFACT is
 * binnengekomen en downloadt deze als .txt-bestand. De order moet minimaal
 * edifactOrderNumber bevatten (edifactRawMessage is optioneel, maar levert een
 * nauwkeuriger DTM+171 in de APERAK op).
 */
export const downloadAperak = async (order) => {
  if (!order?.edifactOrderNumber) {
    message.error(i18n.t("notifications.no_order_data"));
    return;
  }

  try {
    const token = await getFirebaseIDToken();

    const response = await axios.post(
      generateEdifactAperak,
      {
        edifactOrderNumber: order.edifactOrderNumber,
        edifactRawMessage: order.edifactRawMessage,
      },
      { headers: { Authorization: token } },
    );

    const blob = new Blob([response.data.aperak], { type: "text/plain" });
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `APERAK-${order.edifactOrderNumber}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error generating APERAK:", error);
    message.error(error.response?.data?.message || i18n.t("notifications.error_generating_documents"));
  }
};

const escapeHtml = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Eén rij van de kopieerbare veldenlijst: exact het label en formaat van het
 * betreffende Go-webEDI-portaalveld (template "foods_desadv"), zodat Growy
 * de waarde 1-op-1 kan overtypen. `format` bepaalt alleen de weergave, de
 * waarde zelf komt ongewijzigd van de backend (DesadvSummary).
 */
const summaryFieldRow = (label, value) =>
  `<div class="field-row"><span class="field-label">${escapeHtml(label)}</span><span class="field-value">${escapeHtml(
    String(value ?? ""),
  )}</span></div>`;

/**
 * Bouwt de HTML voor de mensleesbare samenvatting (Go-webEDI-portaalvelden)
 * boven de ruwe EDIFACT-tekst — zie DesadvSummary in desadv-generator.ts
 * (backend) voor de brondata. Groepeert exact zoals het portaalformulier:
 * eerst de berichtkop, dan per Despatch Unit (SSCC) de bijbehorende Line Item.
 */
const buildDesadvSummaryHtml = (summary) => {
  if (!summary) return "";

  const headerRows = [
    summaryFieldRow("Despatch Advice Number", summary.despatchAdviceNumber),
    summaryFieldRow("Message Date", summary.messageDate),
    summaryFieldRow("Message Time (HHMM)", summary.messageTime),
    summaryFieldRow("Estimated Delivery Date", summary.estimatedDeliveryDate),
    summaryFieldRow("Estimated Delivery Time (HHMM)", summary.estimatedDeliveryTime),
    summaryFieldRow("Order Number", summary.orderNumber),
    summaryFieldRow("GLN Buyer", summary.buyerGln),
    summaryFieldRow("GLN Delivery Party", summary.deliveryPartyGln),
  ].join("");

  const unitsHtml = (summary.units || [])
    .map(
      (unit, index) => `
      <div class="unit-block">
        <div class="unit-title">Despatch Unit ${index + 1}</div>
        ${summaryFieldRow("GTIN", unit.gtin)}
        ${summaryFieldRow("Delivered Quantity", unit.quantity)}
        ${summaryFieldRow("Batch Number", unit.batchNumber)}
        ${summaryFieldRow("Best Before Date", unit.bestBeforeDate)}
        ${summaryFieldRow("RTI Crate GTIN", unit.crateGtin)}
        ${summaryFieldRow("RTI Crate Quantity", unit.crateQuantity)}
        ${summaryFieldRow("RTI Pallet GTIN", unit.palletGtin)}
      </div>`,
    )
    .join("");

  return `
    <section class="summary">
      <h1>DESADV — velden voor Go-webEDI-portaal</h1>
      <p class="summary-hint">Kopieer onderstaande waarden 1-op-1 naar het "foods_desadv"-formulier in het Go-webEDI-portaal.</p>
      <div class="field-group">${headerRows}</div>
      ${unitsHtml}
    </section>`;
};

/**
 * Opent een stuk platte tekst in een nieuw tabblad, als een blanco pagina met
 * alleen de tekst (word-wrapped, nooit breder dan het scherm) — gebruikt om
 * EDIFACT-berichten (DESADV, etc.) te tonen i.p.v. ze te downloaden. Met
 * `summary` wordt daarboven een mensleesbare kopieerlijst getoond (zie
 * buildDesadvSummaryHtml).
 */
const openTextInNewTab = (text, title, summary = null) => {
  const escapedText = escapeHtml(text);

  const html = `<!DOCTYPE html>
<html lang="nl">
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      body {
        margin: 0;
        padding: 16px;
        background: #1e1e1e;
        color: #d4d4d4;
        font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
      }
      pre {
        white-space: pre-wrap;
        word-break: break-word;
        margin: 0;
        font-size: 13px;
        line-height: 1.5;
      }
      .summary {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        background: #2a2d2e;
        border-radius: 6px;
        padding: 16px 20px;
        margin-bottom: 24px;
      }
      .summary h1 {
        font-size: 16px;
        margin: 0 0 4px;
      }
      .summary-hint {
        font-size: 12px;
        color: #9aa0a6;
        margin: 0 0 16px;
      }
      .field-group {
        display: grid;
        grid-template-columns: 1fr;
        gap: 4px;
        margin-bottom: 16px;
      }
      .unit-block {
        border-top: 1px solid #444;
        padding-top: 10px;
        margin-top: 10px;
      }
      .unit-title {
        font-weight: 600;
        margin-bottom: 6px;
      }
      .field-row {
        display: flex;
        gap: 8px;
        font-size: 13px;
        padding: 2px 0;
      }
      .field-label {
        color: #9aa0a6;
        white-space: nowrap;
      }
      .field-label::after {
        content: ":";
      }
      .field-value {
        font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
        user-select: all;
      }
    </style>
  </head>
  <body>
    ${buildDesadvSummaryHtml(summary)}
    <pre>${escapedText}</pre>
  </body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const blobUrl = window.URL.createObjectURL(blob);
  window.open(blobUrl, "_blank");
  // De nieuwe tab moet de blob nog kunnen laden, dus pas na een korte
  // vertraging weer vrijgeven i.p.v. direct na window.open.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
};

/**
 * Haalt een preview op van het aantal pallets per orderregel (op basis van
 * kratten-per-laag en de bundle-samenstelling), zonder SSCC's te reserveren
 * of iets op te slaan. Gebruikt om het "Genereer DESADV"-formulier te vullen.
 */
export const previewDesadv = async (farmorderId, region) => {
  const token = await getFirebaseIDToken();
  const response = await axios.post(
    previewEdifactDesadv,
    { farmorderId, region },
    { headers: { Authorization: token } },
  );
  return response.data;
};

/**
 * Genereert de DESADV voor een order (eenmalig — de backend weigert dit als
 * er al een edifactDesadvMessage op de order staat) en slaat deze op de order
 * op. Batchnummers worden door de backend automatisch bepaald (product + verzenddatum).
 */
export const generateDesadv = async (farmorderId, region) => {
  const token = await getFirebaseIDToken();
  const response = await axios.post(
    generateEdifactDesadv,
    { farmorderId, region },
    { headers: { Authorization: token } },
  );
  return response.data;
};

/** Verstuurt de opgeslagen DESADV van een order via Descartes. Geeft { tid, sentAt } terug. */
export const sendDesadv = async (farmorderId, region) => {
  const token = await getFirebaseIDToken();
  const response = await axios.post(
    sendEdifactDesadv,
    { farmorderId, region },
    { headers: { Authorization: token } },
  );
  return response.data;
};

/** Verstuurt de opgeslagen INVOIC van een factuur via Descartes. Geeft { tid, sentAt } terug. */
export const sendInvoic = async (invoiceId, region) => {
  const token = await getFirebaseIDToken();
  const response = await axios.post(
    sendEdifactInvoic,
    { invoiceId, region },
    { headers: { Authorization: token } },
  );
  return response.data;
};

/** Toont een eerder gegenereerde DESADV (en de kopieerbare veldenlijst) in een nieuw tabblad. */
export const viewDesadv = (order) => {
  if (!order?.edifactDesadvMessage) {
    message.error(i18n.t("notifications.no_order_data"));
    return;
  }
  openTextInNewTab(
    order.edifactDesadvMessage,
    `DESADV-${order.edifactOrderNumber || order.farmorderId}`,
    order.edifactDesadvSummary,
  );
};

/** Leesbare uitleg per segment van een EDI-bericht, zie api/src/edifact/edifact-describer.ts. */
export const describeEdifact = async (message) => {
  const token = await getFirebaseIDToken();
  const response = await axios.post(describeEdifactMessage, { message }, { headers: { Authorization: token } });
  return response.data.segments;
};
